import { FilesUploader, If, Media } from "components";
import { BYTES_IN_MB } from "constants/common";
import { MediaDirectoryKeys, MediaType } from "constants/enums";
import { DirectUploadMedia } from "libs/directUpload";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FormField, MediaFilePicker } from "ui";

import { WHATSAPP_SAFE_VIDEO_SIZE_IN_MB } from "../../constants";
import { toVideoPreviewMedia } from "../../helpers";
import { UsageGuideVideoFieldProps } from "./UsageGuideVideoField.types";

import styles from "./UsageGuideVideoField.module.scss";

const VIDEO_PREVIEW_SIZE = { width: 260, height: 180 };

const UsageGuideVideoField = ({
  videoUrl,
  isReadOnly = false,
  onChange
}: UsageGuideVideoFieldProps) => {
  const { t } = useTranslation();

  const [isOversized, setIsOversized] = useState(false);

  const videoValue = toVideoPreviewMedia(videoUrl);

  const handleSuccessUploading = (media: DirectUploadMedia) => {
    // Checked after the upload, so the size is the one that actually landed in
    // S3 — the picker compresses on the way in and the raw file size would
    // over-report.
    setIsOversized(media.size > WHATSAPP_SAFE_VIDEO_SIZE_IN_MB * BYTES_IN_MB);
    onChange(media);
  };

  const handleDelete = () => {
    setIsOversized(false);
    onChange(null);
  };

  if (isReadOnly) {
    return (
      <FormField label={t("How-to video")}>
        <If condition={Boolean(videoValue)}>
          <Media
            className={styles.video_preview}
            mediaFile={videoValue as DirectUploadMedia}
            controls
          />
        </If>

        <If condition={!videoValue}>
          <p className={styles.video_empty}>{t("No video")}</p>
        </If>
      </FormField>
    );
  }

  return (
    <FormField label={t("How-to video")}>
      <p className={styles.video_hint}>
        {t(
          "The instructional clip the assistant sends in chat. Not the product's showcase video."
        )}
      </p>

      <FilesUploader
        uploadDirectoryKey={MediaDirectoryKeys.ProductUsageGuide}
        onSuccessUploading={handleSuccessUploading}
      >
        {(uploaderProps) => (
          <MediaFilePicker
            {...uploaderProps}
            label={t("Upload video")}
            allowedMediaTypes={[MediaType.VIDEO]}
            value={videoValue}
            customSize={VIDEO_PREVIEW_SIZE}
            onDeleteMediaFile={handleDelete}
          />
        )}
      </FilesUploader>

      <If condition={isOversized}>
        <p className={styles.video_warning}>
          {t(
            "This clip is over 16 MB. WhatsApp cannot deliver it by link, so the backend will re-compress it on the first send — slow, and lossy. A shorter clip is better."
          )}
        </p>
      </If>
    </FormField>
  );
};

export default UsageGuideVideoField;
